import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CatalogOffering, CatalogService, CatalogWarehouse, InventoryMovement } from './catalog.service';
import { InventoryCostingApiService } from './inventory-costing-api.service';
import { inventoryOperationTypeLabel, isTransferOperationType } from './inventory-movement-labels';

@Component({
  standalone: true,
  selector: 'ain-inventory-movement-detail',
  imports: [AinPageComponent, AinStateComponent, InfoHintComponent, RouterLink, ButtonModule, TableModule, AinCanAccessDirective],
  template: `
    <ain-page [title]="t('catalog.movements.detailTitle')" [description]="t('catalog.movements.detailDescription')">
      <ain-info-hint page-title-addon
        [titleKey]="'catalog.costing.gl.detailHelp.title'"
        [descriptionKey]="'catalog.costing.gl.detailHelp.description'"
        [bulletKeys]="['catalog.costing.gl.detailHelp.bullets.status', 'catalog.costing.gl.detailHelp.bullets.retry']" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (!movement()) {
        <ain-state type="error" [title]="t('catalog.unavailable')" [message]="error() || t('catalog.movements.notFound')" />
      } @else {
        <section class="header-card">
          <div>
            <h3>{{ operationTypeLabel(movement()!.operationType) }}</h3>
            <p>{{ movement()!.referenceNumber || '-' }} • {{ movement()!.status }}</p>
            @if (isTransferOperationType(movement()!.operationType)) {
              <p class="paired-refs">
                {{ t('catalog.movements.pairedIssueRef') }}: {{ movement()!.transferIssueReferenceNumber || '-' }} ·
                {{ t('catalog.movements.pairedReceiptRef') }}: {{ movement()!.transferReceiptReferenceNumber || '-' }}
              </p>
            }
          </div>
          <div class="actions">
            <a [routerLink]="['/inventory/movements']" class="back-link">{{ t('catalog.action.backToList') }}</a>
            @if (isTransferOperationType(movement()!.operationType)) {
              <button pButton type="button" severity="secondary" [label]="t('catalog.movements.printTransfer')" (click)="printTransfer()"></button>
            }
            <button pButton type="button" [label]="t('catalog.action.refresh')" severity="secondary" (click)="load()"></button>
          </div>
        </section>

        <section class="summary-grid">
          <div><strong>{{ t('catalog.movements.idempotencyKey') }}:</strong> {{ movement()!.idempotencyKey }}</div>
          <div><strong>{{ t('catalog.inventory.occurredAt') }}:</strong> {{ movement()!.processedAtUtc }}</div>
        </section>

        <section class="gl-card">
          <h3>{{ t('catalog.costing.gl.title') }}
            <ain-info-hint [titleKey]="'catalog.costing.gl.panelHelp.title'" [descriptionKey]="'catalog.costing.gl.panelHelp.description'" />
          </h3>
          <div class="summary-grid">
            <div><strong>{{ t('catalog.costing.gl.status') }}:</strong> {{ movement()!.glPostingStatus || '-' }}</div>
            <div>
              <strong>{{ t('catalog.costing.gl.journal') }}:</strong>
              @if (movement()!.glJournalEntryId) {
                <a [routerLink]="['/financial/management/journal-entry', movement()!.glJournalEntryId, 'edit']">{{ t('catalog.costing.gl.openJournal') }}</a>
              } @else {
                -
              }
            </div>
            @if (movement()!.glErrorCode) {
              <div class="error-row"><strong>{{ t('catalog.costing.gl.error') }}:</strong> {{ movement()!.glErrorCode }}</div>
            }
          </div>
          @if (canRetryGl()) {
            <button pButton type="button" ainCanAccess [ainCanAccessPermission]="'inventory.movements.post-gl'" [label]="t('catalog.costing.gl.retry')" [disabled]="retrying()" (click)="retryGl()"></button>
          }
        </section>

        <section class="lines-card">
          <h3>{{ t('catalog.movements.linesTitle') }}</h3>
          <p-table [value]="movement()!.lines">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('catalog.inventory.offeringId') }}</th>
                <th>{{ t('catalog.movements.fromWarehouse') }}</th>
                <th>{{ t('catalog.movements.toWarehouse') }}</th>
                <th>{{ t('catalog.inventory.quantity') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ resolveOfferingLabel(row.offeringId) }}</td>
                <td>{{ resolveWarehouseLabel(row.fromWarehouseId) }}</td>
                <td>{{ resolveWarehouseLabel(row.toWarehouseId) }}</td>
                <td>{{ row.quantity }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>

        @if (error()) {
          <p class="error">{{ error() }}</p>
        }
      }
    </ain-page>
  `,
  styles: [`
    .header-card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; display: flex; justify-content: space-between; gap: 12px; align-items: flex-start; }
    .header-card h3 { margin: 0; }
    .header-card p { margin: 6px 0 0; color: #64748b; }
    .paired-refs { font-size: 13px; color: #475569; }
    .actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .back-link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .summary-grid { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; margin-top: 10px; display: grid; gap: 8px; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); }
    .lines-card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; margin-top: 10px; }
    .lines-card h3 { margin: 0 0 10px; }
    .gl-card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; margin-top: 10px; }
    .gl-card h3 { margin: 0 0 10px; display: flex; align-items: center; gap: 6px; }
    .error-row { color: #b91c1c; }
    .error { margin: 10px 0 0; color: #b91c1c; }
  `]
})
export class InventoryMovementDetailComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly costingApi = inject(InventoryCostingApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly retrying = signal(false);
  readonly error = signal('');
  readonly movement = signal<InventoryMovement | null>(null);
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly warehouses = signal<CatalogWarehouse[]>([]);
  readonly isTransferOperationType = isTransferOperationType;
  operationTypeLabel = (operationType: string) => inventoryOperationTypeLabel(operationType, key => this.t(key));

  ngOnInit(): void {
    this.preloadLookupData();
    this.load();
  }

  load(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.loading.set(false);
      this.error.set(this.t('catalog.movements.notFound'));
      return;
    }

    this.loading.set(true);
    this.error.set('');
    this.service.getInventoryMovement(id).subscribe({
      next: movement => {
        this.movement.set(movement);
        this.loading.set(false);
      },
      error: () => {
        this.movement.set(null);
        this.error.set(this.t('catalog.movements.notFound'));
        this.loading.set(false);
      }
    });
  }

  resolveOfferingLabel(offeringId: string | null | undefined): string {
    if (!offeringId) {
      return '-';
    }

    const offering = this.offerings().find(item => item.id === offeringId);
    return offering ? `${offering.code} - ${offering.title}` : '-';
  }

  resolveWarehouseLabel(warehouseId: string | null | undefined): string {
    if (!warehouseId) {
      return '-';
    }

    const warehouse = this.warehouses().find(item => item.id === warehouseId);
    return warehouse ? `${warehouse.code} - ${warehouse.name}` : '-';
  }

  canRetryGl(): boolean {
    const m = this.movement();
    if (!m?.glPostingStatus) return false;
    return m.glPostingStatus === 'Failed' || m.glPostingStatus === 'Pending';
  }

  retryGl(): void {
    const id = this.movement()?.id;
    if (!id) return;
    this.retrying.set(true);
    this.service.retryInventoryMovementGlPosting(id).subscribe({
      next: movement => {
        this.movement.set(movement);
        this.retrying.set(false);
      },
      error: () => {
        this.error.set(this.t('catalog.costing.gl.retryFailed'));
        this.retrying.set(false);
      }
    });
  }

  printTransfer(): void {
    const id = this.movement()?.id;
    if (!id) return;
    this.costingApi.getTransferPrintDocument(id).subscribe({
      next: doc => {
        const body = [
          `${this.t('catalog.movements.transferPrintTitle')}: ${doc.transferReferenceNumber}`,
          `${this.t('catalog.movements.pairedIssueRef')}: ${doc.issueReferenceNumber}`,
          `${this.t('catalog.movements.pairedReceiptRef')}: ${doc.receiptReferenceNumber}`,
          `${this.t('catalog.inventory.quantity')}: ${doc.quantity}`
        ].join('\n');
        const w = window.open('', '_blank');
        if (w) {
          w.document.write(`<pre>${body}</pre>`);
          w.document.close();
          w.print();
        }
      },
      error: () => this.error.set(this.t('catalog.movements.printFailed'))
    });
  }

  private preloadLookupData(): void {
    this.service.listOfferings().subscribe({
      next: offerings => this.offerings.set(offerings),
      error: () => this.offerings.set([])
    });

    this.service.listWarehouses().subscribe({
      next: warehouses => this.warehouses.set(warehouses),
      error: () => this.warehouses.set([])
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
